/**
 * Tours and calls (phase 4, 13 §2, 02 §3.8, 03 §4.3): tour types, the staff member's weekly hours and
 * time off, the slots computed from them, the booked tour with its reminders, briefing and debrief, and
 * the per-venue tour settings. Slot ids are opaque: the API mints them and re-checks them on booking
 * (`SLOT_TAKEN` when the window went in between). Every entity carries `venueId`; no request names one.
 */
import { z } from 'zod';
import { Actor, DateString, Id, Instant, TimeHHmm, Timestamps, Weekday } from './common.js';
// ── Tour types ────────────────────────────────────────────────────────────
/** An in-person walk-through of the spaces, or a phone call with the coordinator. */
export const TourKind = z.enum(['tour', 'call']);
const tourTypeFields = {
    /** Shown to guests ("Tour of the spaces", "15-minute call"). Content: PLACEHOLDER in the seed. */
    name: z.string().trim().min(1).max(120),
    kind: TourKind,
    durationMinutes: z.number().int().min(5).max(480),
    /** Guests (and the concierge's `book_tour`) may book it themselves; otherwise staff book it. */
    selfServe: z.boolean(),
    /** Kept free after (and before) each tour of the same staff member. */
    bufferMinutes: z.number().int().min(0).max(240),
    /** Earliest a slot may start from now (03 §4.1 `min_notice_hours`). */
    minNoticeHours: z.number().int().min(0).max(336),
    active: z.boolean(),
    sort: z.number().int(),
};
export const TourType = z.object({ id: Id, venueId: Id, ...tourTypeFields, ...Timestamps });
/** `POST /s/tour-types`. Omitted: `minNoticeHours` 2, `active` true, `sort` 0. */
export const TourTypeInput = z.object({
    ...tourTypeFields,
    minNoticeHours: tourTypeFields.minNoticeHours.default(2),
    active: z.boolean().default(true),
    sort: z.number().int().default(0),
});
/** `PATCH /s/tour-types/:id`: only the fields sent change (no defaults, unlike `TourTypeInput.partial()`). */
export const TourTypePatch = z.object(tourTypeFields).partial();
/** What a guest sees of a type: only `selfServe` and `active` ones are listed. */
export const GuestTourType = TourType.pick({ id: true, name: true, kind: true, durationMinutes: true });
// ── Staff availability and time off ───────────────────────────────────────
const availabilityFields = {
    staffUserId: Id,
    weekday: Weekday,
    /** Venue-local; `end` is later than `start` on the same day. */
    start: TimeHHmm,
    end: TimeHHmm,
};
/** One weekly window in which a staff member takes tours and calls (B5 default: Sun–Thu 12:00–17:00). */
export const StaffAvailability = z.object({ id: Id, venueId: Id, ...availabilityFields, ...Timestamps });
const startBeforeEnd = (w) => w.start === undefined || w.end === undefined || w.start < w.end;
export const StaffAvailabilityInput = z.object(availabilityFields).refine(startBeforeEnd, {
    message: 'The window must end after it starts',
    path: ['end'],
});
/** `PATCH /s/staff-availability/:id`; the API checks the order against the stored row. */
export const StaffAvailabilityPatch = z
    .object(availabilityFields)
    .partial()
    .refine(startBeforeEnd, {
    message: 'The window must end after it starts',
    path: ['end'],
});
const timeOffFields = {
    staffUserId: Id,
    from: Instant,
    to: Instant,
    reason: z.string().trim().max(200).nullable(),
};
/** A staff member is away `[from, to)`: no slots of theirs inside it. */
export const StaffTimeOff = z.object({ id: Id, venueId: Id, ...timeOffFields, ...Timestamps });
const fromBeforeTo = (t) => t.from === undefined || t.to === undefined || Date.parse(t.from) < Date.parse(t.to);
export const StaffTimeOffInput = z
    .object({ ...timeOffFields, reason: timeOffFields.reason.default(null) })
    .refine(fromBeforeTo, { message: 'Time off must end after it starts', path: ['to'] });
/** `PATCH /s/staff-time-off/:id`; the API checks the order against the stored row. */
export const StaffTimeOffPatch = z
    .object(timeOffFields)
    .partial()
    .refine(fromBeforeTo, { message: 'Time off must end after it starts', path: ['to'] });
// ── Slots ─────────────────────────────────────────────────────────────────
/**
 * Opaque, minted by the API (staff + start + type, signed or looked up: the API's choice). Clients and
 * the model pass it back as they got it; a slot not returned by a slots read is refused.
 */
export const TourSlotId = z
    .string()
    .regex(/^[A-Za-z0-9_.:~-]{1,256}$/, 'Expected a slot id as returned by a slots read');
/**
 * A bookable window: the staff member's weekly hours minus time off, existing tours (+buffer), the
 * calendar's busy blocks and closures; never in the past or inside `minNoticeHours`.
 */
export const TourSlot = z.object({
    slotId: TourSlotId,
    staffUserId: Id,
    tourTypeId: Id,
    startsAt: Instant,
    endsAt: Instant,
});
/** A slots read: venue-local days `[from, to]` (the API clamps to 31 days). */
export const TourSlotsQuery = z.object({ typeId: Id, from: DateString, to: DateString });
/** Slots in start order, with the venue timezone the guest app lays the week strip out in. */
export const TourSlotList = z.object({ timezone: z.string(), items: z.array(TourSlot) });
// ── Tour ──────────────────────────────────────────────────────────────────
/**
 * `booked` and `rescheduled` are both on (the latter moved at least once: the same row, the calendar
 * event moved); `cancelled`, `no_show` and `done` are final.
 */
export const TourStatus = z.enum(['booked', 'rescheduled', 'cancelled', 'no_show', 'done']);
/** The statuses of a tour that is still going to happen. */
export const ACTIVE_TOUR_STATUSES = ['booked', 'rescheduled'];
export const DebriefOutcome = z.enum(['hot', 'warm', 'cold', 'lost']);
/** Where the debrief came from: the staff member's reply to the debrief text, or the lead page form. */
export const DebriefSource = z.enum(['staff_sms', 'console']);
/** What a debrief records (`record_debrief`, `PUT /s/tours/:id/debrief`). */
export const TourDebriefInput = z.object({
    /** `false` marks the tour `no_show` (and wakes `tour_no_show`); `true` marks it `done`. */
    attended: z.boolean(),
    outcome: DebriefOutcome,
    /** The next follow-up the staff member asked for ("chase in a week"); null: the concierge's usual cadence. */
    nextReviewAt: Instant.nullable().default(null),
    /** Other venues the guest is seeing; merged into `lead.competingVenues`. */
    competingVenues: z.array(z.string().trim().min(1).max(120)).max(20).default([]),
    /** The staff member's words, kept as they said them. */
    notes: z.string().trim().max(2000).default(''),
});
export const TourDebrief = z.object({
    attended: z.boolean(),
    outcome: DebriefOutcome,
    nextReviewAt: Instant.nullable(),
    competingVenues: z.array(z.string()),
    notes: z.string(),
    recordedBy: Actor,
    recordedAt: Instant,
    source: DebriefSource,
});
export const Tour = z.object({
    id: Id,
    venueId: Id,
    leadId: Id,
    tourTypeId: Id,
    staffUserId: Id,
    startsAt: Instant,
    endsAt: Instant,
    status: TourStatus,
    /** The event on the connected calendar (memory adapter: its own id); null until mirrored. */
    calendarEventId: z.string().max(1024).nullable(),
    /** When the day-before reminder went; null until it has. */
    reminderDayBeforeAt: Instant.nullable(),
    /** When the two-hours-before reminder went; null until it has (or when `reminder2h` is off). */
    reminder2hAt: Instant.nullable(),
    /** When the five-line briefing reached the staff member. */
    briefSentAt: Instant.nullable(),
    /** When the "how did it go?" question went. */
    debriefAskedAt: Instant.nullable(),
    debrief: TourDebrief.nullable(),
    /** Opaque, for the one-tap reschedule link (`?reschedule=<tourId>&t=<token>`). Never logged. */
    rescheduleToken: z.string().min(16).max(200),
    /** Who booked it: the guest, the concierge, or a staff member. */
    bookedBy: Actor,
    cancelReason: z.string().max(500).nullable(),
    ...Timestamps,
});
/** A tour with the labels the console's lists show. */
export const TourListItem = Tour.extend({
    leadLabel: z.string(),
    typeName: z.string(),
    kind: TourKind,
    staffName: z.string().nullable(),
});
/** A tour on the console calendar's `tours` layer (`CalendarResponse.tours`). */
export const CalendarTour = z.object({
    id: Id,
    leadId: Id,
    leadLabel: z.string(),
    tourTypeId: Id,
    typeName: z.string(),
    kind: TourKind,
    staffUserId: Id,
    staffName: z.string().nullable(),
    startsAt: Instant,
    endsAt: Instant,
    status: TourStatus,
});
/**
 * A lead's tours at a glance (`LeadListItem.tours`, `LeadDetail.tours`): the next one on, and the counts
 * of the rest. A booked tour or call is a badge, not a stage (03 §4.5). Lives here, not in leads.ts, so
 * leads.ts (which the guest bundle reads) does not pull the tour module in.
 */
export const LeadToursBadge = z.object({
    /** The earliest `booked`/`rescheduled` tour that has not ended; null when none. */
    next: z.object({ id: Id, kind: TourKind, startsAt: Instant, status: TourStatus }).nullable(),
    total: z.number().int().min(0),
    done: z.number().int().min(0),
    noShow: z.number().int().min(0),
});
/** The guest's own tour: no staff, no debrief, no calendar ids. */
export const GuestTour = z.object({
    id: Id,
    tourTypeId: Id,
    typeName: z.string(),
    kind: TourKind,
    startsAt: Instant,
    endsAt: Instant,
    status: TourStatus,
    /** For the Talk scene's reschedule, the same token the one-tap link carries. */
    rescheduleToken: z.string().min(16).max(200),
});
// ── Settings (one per venue, `settings.tours`) ────────────────────────────
/**
 * When the tour jobs fire (13 §3, §10 defaults). `briefMinutesBefore` supersedes
 * `ChannelSettings.tourBriefMinutesBefore` and `SettingsValues.tourBriefMinutesBefore`, which stay for
 * compatibility and are no longer read.
 */
export const TourSettings = z.object({
    /** The five-line briefing goes this long before the start (`tour.brief`). */
    briefMinutesBefore: z.number().int().min(0).max(1440),
    /** The debrief question goes this long after the end (`tour.debrief`). */
    debriefMinutesAfter: z.number().int().min(0).max(1440),
    /** Venue-local hour (0–23) of the day-before reminder. */
    reminderDayBeforeHour: z.number().int().min(0).max(23),
    /** Also remind two hours before. */
    reminder2h: z.boolean(),
    /** A tour not marked done this long after its start counts toward a no-show prompt. */
    noShowGraceMinutes: z.number().int().min(0).max(240),
    /** IANA timezone slots and reminders are computed in. */
    tz: z.string().min(1).max(64),
});
export const DEFAULT_TOUR_SETTINGS = {
    briefMinutesBefore: 30,
    debriefMinutesAfter: 30,
    reminderDayBeforeHour: 18,
    reminder2h: true,
    noShowGraceMinutes: 15,
    tz: 'America/New_York',
};
// ── The Talk scene ────────────────────────────────────────────────────────
/**
 * `show('talk', params)`: open the Talk scene on a type and a week. `tourType` is a tour type id, or a
 * kind (`tour`/`call`) meaning the first active self-serve type of it.
 */
export const TalkSceneParams = z.object({
    tourType: z.union([Id, TourKind]).optional(),
    from: DateString.optional(),
});
//# sourceMappingURL=tours.js.map